import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useCart } from '../../hooks/useCart';

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { cart, openCart } = useCart();

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const navLinks = [
    { name: 'Home', path: '/', icon: 'ri-home-4-line' },
    { name: 'Videos', path: '/videos', icon: 'ri-play-circle-line' },
    { name: 'Memes', path: '/memes', icon: 'ri-emotion-laugh-line' },
    { name: 'Shop', path: '/shop', icon: 'ri-shopping-bag-3-line' },
    { name: 'Adopt', path: '/adopt', icon: 'ri-heart-pulse-line' },
    { name: 'Blog', path: '/blog', icon: 'ri-article-line' },
  ];

  const cartItemCount = cart?.totalQuantity || 0;

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 sm:gap-3 group">
              <img
                src="https://public.readdy.ai/ai/img_res/37bc7df8-b441-456a-b64e-dfe2e72930b6.png"
                alt="MeowCentral Logo"
                className="w-9 h-9 sm:w-12 sm:h-12 object-contain transition-transform duration-300 group-hover:rotate-12"
              />
              <span className="font-heading font-bold text-lg sm:text-xl text-primary">
                MeowCentral
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              <Link 
                to="/" 
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  location.pathname === '/' 
                    ? 'text-teal-600 font-semibold' 
                    : 'text-gray-700 hover:text-teal-600'
                }`}
              >
                Home
              </Link>
              <Link 
                to="/videos" 
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  location.pathname === '/videos' 
                    ? 'text-teal-600 font-semibold' 
                    : 'text-gray-700 hover:text-teal-600'
                }`}
              >
                Videos
              </Link>
              <Link 
                to="/memes" 
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  location.pathname === '/memes' 
                    ? 'text-teal-600 font-semibold' 
                    : 'text-gray-700 hover:text-teal-600'
                }`}
              >
                😹 Memes
              </Link>
              <Link 
                to="/shop" 
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  location.pathname === '/shop' || location.pathname.startsWith('/shop/')
                    ? 'text-teal-600 font-semibold' 
                    : 'text-gray-700 hover:text-teal-600'
                }`}
              >
                Shop
              </Link>
              <Link 
                to="/adopt" 
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  location.pathname === '/adopt' || location.pathname.startsWith('/adopt/')
                    ? 'text-teal-600 font-semibold' 
                    : 'text-gray-700 hover:text-teal-600'
                }`}
              >
                Adopt
              </Link>
              <Link 
                to="/blog" 
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  location.pathname === '/blog' || location.pathname.startsWith('/blog/')
                    ? 'text-teal-600 font-semibold' 
                    : 'text-gray-700 hover:text-teal-600'
                }`}
              >
                Blog
              </Link>
            </nav>

            {/* Right Actions */}
            <div className="flex items-center gap-2 sm:gap-3">
              <Link
                to="/shop"
                className="hidden sm:flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent/90 text-white font-semibold text-sm rounded-lg transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-shopping-bag-line"></i>
                Shop Now
              </Link>
              <button
                onClick={openCart}
                className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-gray-100 text-gray-700 transition-all duration-300 hover:scale-110 cursor-pointer relative"
                aria-label="Shopping Cart"
              >
                <i className="ri-shopping-cart-line text-lg"></i>
                {cartItemCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-accent text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {cartItemCount > 99 ? '99+' : cartItemCount}
                  </span>
                )}
              </button>

              {/* Mobile Hamburger */}
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-700 transition-all duration-300 cursor-pointer"
                aria-label="Open menu"
              >
                <i className="ri-menu-3-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Drawer */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-[60] lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="fixed top-0 right-0 bottom-0 w-72 bg-white z-[70] lg:hidden shadow-2xl flex flex-col"
            >
              {/* Drawer Header */}
              <div className="flex items-center justify-between p-5 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <img
                    src="https://public.readdy.ai/ai/img_res/37bc7df8-b441-456a-b64e-dfe2e72930b6.png"
                    alt="Logo"
                    className="w-8 h-8 object-contain"
                  />
                  <span className="font-heading font-bold text-primary text-lg">Menu</span>
                </div>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              {/* Nav Links */}
              <div className="flex-1 overflow-y-auto py-4">
                {navLinks.map((link, index) => (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link
                      to={link.path}
                      className={`flex items-center gap-3 px-6 py-3.5 font-body font-medium text-sm transition-all ${
                        location.pathname === link.path
                          ? 'text-primary bg-primary/5 border-r-3 border-primary'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-primary'
                      }`}
                    >
                      <i className={`${link.icon} text-lg w-5 h-5 flex items-center justify-center`}></i>
                      {link.name}
                    </Link>
                  </motion.div>
                ))}
              </div>

              {/* Drawer Footer */}
              <div className="p-5 border-t border-gray-100">
                <Link
                  to="/shop"
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-accent hover:bg-accent/90 text-white font-semibold text-sm rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer mb-4"
                >
                  <i className="ri-shopping-bag-line"></i>
                  Shop Now
                </Link>
                <div className="flex gap-3 justify-center">
                  {['instagram', 'youtube', 'facebook', 'twitter'].map((social) => (
                    <a
                      key={social}
                      href={`https://${social}.com`}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:text-primary hover:bg-primary/10 transition-all cursor-pointer"
                    >
                      <i className={`ri-${social}-line text-base`}></i>
                    </a>
                  ))}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}